import type { FormEvent, ReactNode } from 'react';
import { useState } from 'react';
import { Link } from 'react-router-dom';

type FormData = {
  company: string;
  name: string;
  email: string;
  message: string;
};

type SubmitStatus = 'idle' | 'confirm' | 'submitting' | 'success' | 'error';

const initialFormData: FormData = {
  company: '',
  name: '',
  email: '',
  message: '',
};

function Modal({
  isOpen,
  title,
  children,
}: {
  isOpen: boolean;
  title: string;
  children: ReactNode;
}) {
  if (!isOpen) {
    return null;
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-6 backdrop-blur-sm">
      <div className="w-full max-w-2xl rounded-2xl bg-white p-8 shadow-2xl">
        <h3 className="mb-6 text-2xl font-bold text-gray-800">{title}</h3>
        {children}
      </div>
    </div>
  );
}

export default function ContactSection() {
  const [agreed, setAgreed] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<SubmitStatus>('idle');
  const [submitError, setSubmitError] = useState('');
  const [charCount, setCharCount] = useState(0);
  const [formData, setFormData] = useState<FormData>(initialFormData);

  const handleOpenConfirm = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const form = event.currentTarget;
    if (!form.reportValidity()) {
      return;
    }

    if (!agreed) {
      setSubmitError('プライバシーポリシーへの同意が必要です。');
      setSubmitStatus('error');
      return;
    }

    if (formData.message.length > 500) {
      setSubmitError('お問い合わせ内容は500文字以内で入力してください。');
      setSubmitStatus('error');
      return;
    }

    setSubmitError('');
    setSubmitStatus('confirm');
  };

  const handleConfirmSubmit = async () => {
    setSubmitStatus('submitting');
    setSubmitError('');

    try {
      const data = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        data.append(key, value);
      });
      data.append('privacy', agreed ? '同意する' : '');

      const response = await fetch('/send.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: data.toString(),
      });

      if (!response.ok) {
        throw new Error('send_failed');
      }

      setSubmitStatus('success');
      setFormData(initialFormData);
      setCharCount(0);
      setAgreed(false);
    } catch (error) {
      console.error('送信エラー:', error);
      setSubmitError('送信に失敗しました。しばらくしてから再度お試しください。');
      setSubmitStatus('error');
    }
  };

  return (
    <section id="contact" className="relative py-20 md:py-28 overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0">
        <img
          src="https://readdy.ai/api/search-image?query=business%20professionals%20in%20a%20modern%20conference%20room%20having%20a%20meeting%20around%20a%20large%20table%2C%20collaborative%20discussion%2C%20professional%20corporate%20environment%2C%20warm%20lighting%2C%20Japanese%20office%20setting%2C%20teamwork%20atmosphere&width=1920&height=900&seq=contact001&orientation=landscape"
          alt="お問い合わせ背景"
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gray-900/70" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
          {/* Left */}
          <div className="flex flex-col gap-6 pt-4">
            <div className="flex items-center gap-2">
              <span className="w-2 h-2 bg-yellow-400 rounded-full" />
              <span className="text-yellow-400 text-xs font-bold tracking-widest uppercase">CONTACT</span>
            </div>
            <h2 className="text-white font-bold text-4xl md:text-5xl leading-tight">
              お気軽に、<br />ご相談ください。
            </h2>
            <p className="text-gray-300 text-base leading-relaxed">
              まずはお電話またはフォームにて<br />お問い合わせください。<br />
              初回相談は無料で承っております。
            </p>
            <div className="flex flex-col gap-4 mt-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 flex items-center justify-center rounded-full bg-yellow-400/20 border border-yellow-400/40">
                  <i className="ri-map-pin-line text-yellow-400" />
                </div>
                <span className="text-gray-300 text-sm">〒530-0043 大阪府大阪市北区天満1丁目4-19 キオプラザ天満橋</span>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 flex items-center justify-center rounded-full bg-yellow-400/20 border border-yellow-400/40">
                  <i className="ri-phone-line text-yellow-400" />
                </div>
                <span className="text-gray-300 text-sm">050-8896-2380</span>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 flex items-center justify-center rounded-full bg-yellow-400/20 border border-yellow-400/40">
                  <i className="ri-mail-line text-yellow-400" />
                </div>
                <span className="text-gray-300 text-sm">keiri@master-mind-group.com</span>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 flex items-center justify-center rounded-full bg-yellow-400/20 border border-yellow-400/40">
                  <i className="ri-time-line text-yellow-400" />
                </div>
                <span className="text-gray-300 text-sm">平日 9:00〜18:00</span>
              </div>
            </div>
          </div>

          {/* Form */}
          <div className="bg-white/95 rounded-2xl p-8">
            <form
              id="contact-form"
              onSubmit={handleOpenConfirm}
              className="flex flex-col gap-4"
            >
                <h3 className="text-gray-800 font-bold text-lg mb-1">お問い合わせフォーム</h3>

                <div className="flex flex-col gap-1">
                  <label className="text-gray-600 text-xs font-medium">会社名</label>
                  <input
                    type="text"
                    name="company"
                    placeholder="株式会社〇〇"
                    value={formData.company}
                    onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                    className="border border-gray-200 rounded-lg px-4 py-2.5 text-sm text-gray-800 focus:outline-none focus:border-yellow-400 transition-colors"
                  />
                </div>

                <div className="flex flex-col gap-1">
                  <label className="text-gray-600 text-xs font-medium">お名前 <span className="text-red-400">*</span></label>
                  <input
                    type="text"
                    name="name"
                    required
                    placeholder="山田 太郎"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="border border-gray-200 rounded-lg px-4 py-2.5 text-sm text-gray-800 focus:outline-none focus:border-yellow-400 transition-colors"
                  />
                </div>

                <div className="flex flex-col gap-1">
                  <label className="text-gray-600 text-xs font-medium">メールアドレス <span className="text-red-400">*</span></label>
                  <input
                    type="email"
                    name="email"
                    required
                    placeholder="example@company.co.jp"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="border border-gray-200 rounded-lg px-4 py-2.5 text-sm text-gray-800 focus:outline-none focus:border-yellow-400 transition-colors"
                  />
                </div>

                <div className="flex flex-col gap-1">
                  <label className="text-gray-600 text-xs font-medium">お問い合わせ内容 <span className="text-red-400">*</span></label>
                  <textarea
                    name="message"
                    required
                    rows={4}
                    maxLength={500}
                    placeholder="ご相談内容をご記入ください（500文字以内）"
                    value={formData.message}
                    onChange={(e) => {
                      setFormData({ ...formData, message: e.target.value });
                      setCharCount(e.target.value.length);
                    }}
                    className="border border-gray-200 rounded-lg px-4 py-2.5 text-sm text-gray-800 focus:outline-none focus:border-yellow-400 transition-colors resize-none"
                  />
                  <span className={`text-xs text-right ${charCount >= 500 ? 'text-red-400' : 'text-gray-400'}`}>
                    {charCount}/500
                  </span>
                </div>

                {/* Privacy Checkbox */}
                <div className="flex items-start gap-3 mt-1">
                  <input
                    type="checkbox"
                    id="privacy-agree"
                    checked={agreed}
                    onChange={(e) => setAgreed(e.target.checked)}
                    className="mt-0.5 w-4 h-4 accent-yellow-400 cursor-pointer shrink-0"
                  />
                  <label htmlFor="privacy-agree" className="text-gray-600 text-xs leading-relaxed cursor-pointer">
                    <Link to="/privacy-policy" className="text-yellow-600 underline hover:text-yellow-500" target="_blank">
                      プライバシーポリシー
                    </Link>
                    に同意する
                  </label>
                </div>

                <button
                  type="submit"
                  disabled={charCount > 500}
                  className="mt-2 bg-gray-900 text-white font-semibold py-3 rounded-full hover:bg-gray-700 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  <span>入力内容を確認する</span>
                  <i className="ri-arrow-right-line" />
                </button>
            </form>
          </div>
        </div>
      </div>

      <Modal isOpen={submitStatus === 'confirm'} title="入力内容の確認">
        <div className="space-y-4 text-sm text-gray-700">
          <div className="rounded-2xl border border-gray-200 bg-gray-50 p-5">
            <div className="grid gap-4 md:grid-cols-2">
              <div>
                <div className="mb-1 text-gray-500">会社名</div>
                <div>{formData.company || '未入力'}</div>
              </div>
              <div>
                <div className="mb-1 text-gray-500">お名前</div>
                <div>{formData.name}</div>
              </div>
              <div className="md:col-span-2">
                <div className="mb-1 text-gray-500">メールアドレス</div>
                <div>{formData.email}</div>
              </div>
              <div className="md:col-span-2">
                <div className="mb-1 text-gray-500">お問い合わせ内容</div>
                <div className="whitespace-pre-wrap leading-relaxed">{formData.message}</div>
              </div>
              <div className="md:col-span-2">
                <div className="mb-1 text-gray-500">プライバシーポリシー同意</div>
                <div>{agreed ? '同意する' : '未同意'}</div>
              </div>
            </div>
          </div>
          <div className="flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
            <button
              type="button"
              onClick={() => setSubmitStatus('idle')}
              className="rounded-xl border border-gray-300 px-5 py-3 text-gray-700 transition hover:bg-gray-100"
            >
              修正する
            </button>
            <button
              type="button"
              onClick={handleConfirmSubmit}
              className="rounded-xl bg-yellow-400 px-5 py-3 font-semibold text-gray-900 transition hover:bg-yellow-300"
            >
              この内容で送信する
            </button>
          </div>
        </div>
      </Modal>

      <Modal isOpen={submitStatus === 'submitting'} title="送信中">
        <div className="space-y-4 text-sm text-gray-700">
          <p>お問い合わせを送信しています。しばらくお待ちください。</p>
          <div className="h-2 overflow-hidden rounded-full bg-gray-200">
            <div className="h-full w-1/2 animate-pulse rounded-full bg-yellow-400"></div>
          </div>
        </div>
      </Modal>

      <Modal isOpen={submitStatus === 'success'} title="送信完了">
        <div className="space-y-6 text-sm text-gray-700">
          <p>お問い合わせありがとうございます。担当者より折り返しご連絡いたします。</p>
          <div className="flex justify-end">
            <button
              type="button"
              onClick={() => setSubmitStatus('idle')}
              className="rounded-xl bg-yellow-400 px-5 py-3 font-semibold text-gray-900 transition hover:bg-yellow-300"
            >
              閉じる
            </button>
          </div>
        </div>
      </Modal>

      <Modal isOpen={submitStatus === 'error'} title="送信エラー">
        <div className="space-y-6 text-sm text-gray-700">
          <p>{submitError}</p>
          <div className="flex justify-end">
            <button
              type="button"
              onClick={() => setSubmitStatus('idle')}
              className="rounded-xl border border-gray-300 px-5 py-3 text-gray-700 transition hover:bg-gray-100"
            >
              閉じる
            </button>
          </div>
        </div>
      </Modal>
    </section>
  );
}
